import {
  CAMPAIGN_AUDIENCE_LIMITS,
  type AudienceReachabilityPreview,
  type CampaignAudienceMode,
  type CampaignChannelKind,
  type CampaignPurpose,
  type ContactRecord,
  type SegmentFilter,
} from '@workspace/types';
import { Button } from '@/components/ui/button';
import { ContactAgentSegmentFields } from './ContactAgentSegmentFields';
import { CampaignReachFunnel } from './CampaignReachFunnel';
import { CampaignAudiencePreviewLists } from './CampaignAudiencePreviewLists';
import { formatCampaignMoney } from './ContactAgentMetricCard.utils';
import { channelDataLabel } from '../api/campaignWizardRules';
import {
  cobranzasSegmentActive,
  contactInvoiceTotals,
  contactMatchesCobranzasSegment,
  previewWithInclusions,
} from '../api/campaignAudienceInclusion';

export function CampaignWizardAudienceStep({
  purpose,
  channelKind,
  audienceMode,
  onAudienceMode,
  segmentFilter,
  onSegmentFilter,
  contacts,
  selectedIds,
  onSelectedIds,
  reachPreview,
  onPreview,
  includedIds,
  onIncludedIds,
}: {
  purpose: CampaignPurpose;
  channelKind: CampaignChannelKind;
  audienceMode: CampaignAudienceMode;
  onAudienceMode: (mode: CampaignAudienceMode) => void;
  segmentFilter: SegmentFilter;
  onSegmentFilter: (next: SegmentFilter) => void;
  contacts: ContactRecord[];
  selectedIds: string[];
  onSelectedIds: (ids: string[]) => void;
  reachPreview: AudienceReachabilityPreview | null;
  onPreview: () => void;
  includedIds: string[];
  onIncludedIds: (ids: string[]) => void;
}) {
  const explicitContacts =
    purpose === 'COBRANZAS' && cobranzasSegmentActive(segmentFilter)
      ? contacts.filter(contact => contactMatchesCobranzasSegment(contact, segmentFilter))
      : contacts;
  const explicitIds = explicitContacts
    .map(contact => contact.id)
    .slice(0, CAMPAIGN_AUDIENCE_LIMITS.maxMembersPerRequest);
  const allSelected = explicitIds.length > 0 && explicitIds.every(id => selectedIds.includes(id));
  const channelLabel = channelDataLabel(channelKind);

  return (
    <div className='md:col-span-2 grid gap-6 lg:grid-cols-5'>
      <div className='space-y-4 lg:col-span-2'>
        <label className='flex items-center gap-3 rounded-xl border px-4 py-3 text-sm'>
          <input type='radio' checked={audienceMode === 'SEGMENT'} onChange={() => onAudienceMode('SEGMENT')} />
          Segmento (se materializa al guardar, no se envía)
        </label>
        <label className='flex items-center gap-3 rounded-xl border px-4 py-3 text-sm'>
          <input type='radio' checked={audienceMode === 'EXPLICIT'} onChange={() => onAudienceMode('EXPLICIT')} />
          Lista explícita de contactos
        </label>
        {purpose === 'COBRANZAS' ? (
          <ContactAgentSegmentFields value={segmentFilter} onChange={onSegmentFilter} />
        ) : null}
        {audienceMode === 'SEGMENT' && purpose === 'AVISO' ? (
          <p className='text-sm text-muted-foreground'>Aviso toma la cartera actual. Sin filtros de deuda.</p>
        ) : null}
        <Button type='button' variant='outline' size='sm' onClick={onPreview}>
          Vista previa
        </Button>
        {audienceMode === 'EXPLICIT' ? (
          <ExplicitContactList
            contacts={explicitContacts}
            selectedIds={selectedIds}
            allSelected={allSelected}
            explicitIds={explicitIds}
            overdueOnly={segmentFilter.overdueOnly !== false}
            onSelectedIds={onSelectedIds}
          />
        ) : null}
      </div>
      <div className='space-y-4 lg:col-span-3'>
        {reachPreview ? (
          <>
            <CampaignReachFunnel
              preview={previewWithInclusions(
                reachPreview,
                audienceMode === 'SEGMENT' ? includedIds : selectedIds,
              )}
              channelLabel={channelLabel}
            />
            <CampaignAudiencePreviewLists
              channelKind={channelKind}
              reachable={reachPreview.reachablePreview ?? []}
              missingUnreachable={
                channelKind === 'EMAIL'
                  ? (reachPreview.missingEmailPreview ?? [])
                  : (reachPreview.missingPhonePreview ?? [])
              }
              reachableTotal={reachPreview.reachableCount}
              missingUnreachableTotal={reachPreview.missingPhoneCount ?? reachPreview.unreachableCount}
              reachableAmount={reachPreview.reachableTotalBalance}
              missingUnreachableAmount={reachPreview.missingPhoneTotalBalance}
              includedIds={audienceMode === 'SEGMENT' ? includedIds : selectedIds}
              onIncludedIds={audienceMode === 'SEGMENT' ? onIncludedIds : onSelectedIds}
            />
          </>
        ) : (
          <p className='text-sm text-muted-foreground'>
            Vista previa aplica monto y cantidad de facturas: quién entra con {channelLabel} y quién queda afuera por
            no tenerlo en la cartera.
          </p>
        )}
      </div>
    </div>
  );
}

function ExplicitContactList({
  contacts,
  selectedIds,
  allSelected,
  explicitIds,
  overdueOnly,
  onSelectedIds,
}: {
  contacts: ContactRecord[];
  selectedIds: string[];
  allSelected: boolean;
  explicitIds: string[];
  overdueOnly: boolean;
  onSelectedIds: (ids: string[]) => void;
}) {
  const selectedTotal = contacts
    .filter(contact => selectedIds.includes(contact.id))
    .reduce((sum, contact) => sum + contactInvoiceTotals(contact.debts, overdueOnly).totalBalance, 0);
  return (
    <div className='space-y-2'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p className='text-sm text-muted-foreground'>
          Hasta {CAMPAIGN_AUDIENCE_LIMITS.maxMembersPerRequest} contactos por lote.
        </p>
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={explicitIds.length === 0}
          onClick={() => onSelectedIds(allSelected ? [] : explicitIds)}
        >
          {allSelected ? 'Quitar selección' : `Seleccionar todos (${explicitIds.length})`}
        </Button>
      </div>
      <div className='max-h-80 overflow-auto rounded-xl border'>
        {contacts.map(contact => {
          const totals = contactInvoiceTotals(contact.debts, overdueOnly);
          return (
            <label key={contact.id} className='flex items-start gap-3 border-b px-3 py-2 text-sm last:border-0'>
              <input
                type='checkbox'
                className='mt-1'
                checked={selectedIds.includes(contact.id)}
                onChange={() =>
                  onSelectedIds(
                    selectedIds.includes(contact.id)
                      ? selectedIds.filter(id => id !== contact.id)
                      : [...selectedIds, contact.id].slice(0, CAMPAIGN_AUDIENCE_LIMITS.maxMembersPerRequest),
                  )
                }
              />
              <span>
                <span className='block'>
                  {contact.businessName || `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'Contacto'}
                </span>
                <span className='block text-xs text-muted-foreground'>
                  {formatCampaignMoney(String(totals.totalBalance))} · {totals.invoiceCount} factura
                  {totals.invoiceCount === 1 ? '' : 's'}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <p className='text-sm text-muted-foreground'>
        {selectedIds.length} seleccionados · {formatCampaignMoney(String(selectedTotal))}
      </p>
    </div>
  );
}
